import {
  AuthContext,
  AuthStatus,
  LOCAL_STORAGE_LD_USER_IDENTIFIERS,
  MMKeycloakProfile,
  defaultContext,
} from '@components/AuthContext';
import { flagDefaultsForLocalDev } from '@components/Flag/flags';
import { useFlagMe418020EnableUserProfileGateBeforeRender } from '@generated/flags/ME-418020-enable-user-profile-gate-before-render';
import {
  AuthClient,
  AuthClientError,
  AuthClientEvent,
} from '@react-keycloak/core/lib/types';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import {
  HAS_MASTERY_CHROME_EXTENSION,
  IS_LOCAL_DEV,
  IS_NOT_PROD_ENV,
  SKIP_AUTH,
} from '@utils/constants';
import { jsonParse, jsonStringify } from '@utils/json';
import { removeLocalStorage } from '@utils/localStorage';
import { reportCustomError } from '@utils/reportCustomError';
import { HAS_WINDOW, win } from '@utils/win';
import Keycloak, {
  KeycloakInitOptions,
  KeycloakTokenParsed,
} from 'keycloak-js';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { noop } from 'lodash-es';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { crossAppConfig } from '../../config';
import { IS_CONNECT_CARRIER_LOCAL_OR_CI_CYPRESS } from '../../connect-carrier/harness/constants';
import { IS_CONNECT_CUSTOMER_LOCAL_OR_CI_CYPRESS } from '../../connect-customer/harness/constants';
import {
  APP_KEYCLOAK_MIN_VALIDITY_SECONDS_SYMBOL,
  APP_KEYCLOAK_TRIGGER_REFRESH_SECONDS_SYMBOL,
} from '../GlobalVariables/constants';
import { getGlobalVariable } from '../GlobalVariables/util';
import {
  clearStoredTokens,
  isPrimaryBrowser,
  loadTokensFromStorage,
  markAsPrimaryBrowser,
  saveTokensToStorage,
} from './tokenStorage';
import { setUserData } from './user';

interface KeycloakTokenExt extends KeycloakTokenParsed {
  auth0_id?: string;
  keycloak_user_id?: string;
}

interface AuthState {
  status: AuthStatus;
  user: MMKeycloakProfile | null;
  ldIdentified: boolean;
}

const testLog = (...args: anyOk[]): void => {
  if (IS_NOT_PROD_ENV) {
    // eslint-disable-next-line no-console
    console.debug(...args);
  }
};

let keycloakInstanceObj = null as unknown as Keycloak.KeycloakInstance;
if (HAS_WINDOW) {
  try {
    keycloakInstanceObj = new Keycloak({
      realm: crossAppConfig.keycloakRealm,
      url: crossAppConfig.keycloakUri,
      clientId: crossAppConfig.keycloakClientId,
    });
  } catch (err) {
    const errMsg =
      'Creation of the Keycloak Instance Object failed with these config settings: ' +
      jsonStringify({
        realm: crossAppConfig.keycloakRealm,
        url: crossAppConfig.keycloakUri,
        clientId: crossAppConfig.keycloakClientId,
      });
    reportCustomError(errMsg);
    testLog(errMsg);
  }
}

// React 18 Strict Mode double-mounts components in local dev mode. This causes
// a double initialization of Keycloak, which triggers an error. Guard against
// this by replacing the init method if it has already been called. Do not change
// how this behaves in production.
let originalInit: typeof keycloakInstanceObj.init;
let isInitialized = false;
// Track if this browser session restored tokens from server (secondary browser)
// Used to prevent marking secondary browsers as primary when they receive tokens
let didRestoreTokensFromServer = false;
if (IS_LOCAL_DEV && keycloakInstanceObj && !SKIP_AUTH) {
  originalInit = keycloakInstanceObj.init.bind(keycloakInstanceObj);
  keycloakInstanceObj.init = async function (
    initOptions: KeycloakInitOptions,
  ): Promise<boolean> {
    if (isInitialized) {
      // eslint-disable-next-line no-console
      console.warn(
        'Keycloak instance already initialized, skipping duplicate initialization',
      );
      return Promise.resolve(true);
    }
    isInitialized = true;

    // Check if this browser previously performed a login (primary browser)
    // Primary browser uses normal keycloak flow, secondary browsers restore tokens
    if (isPrimaryBrowser()) {
      testLog('Primary browser detected, using normal keycloak flow');
      return originalInit(initOptions);
    }

    // Try to restore tokens from server for cross-browser token reuse (secondary browser)
    const storedTokens = await loadTokensFromStorage();
    if (storedTokens) {
      try {
        // Mark that this browser restored tokens - it should not become primary
        didRestoreTokensFromServer = true;
        testLog(
          'Secondary browser: Restored Keycloak tokens from server for cross-browser reuse',
        );
        // Pass tokens directly to init() - this is the correct way to bootstrap keycloak-js
        // When bootstrapping with tokens:
        // - Disable onLoad to avoid triggering SSO
        // - Disable checkLoginIframe because there's no active server session for this browser
        // - Remove silentCheckSsoRedirectUri to avoid session checks
        const modifiedOptions: KeycloakInitOptions = {
          ...initOptions,
          onLoad: undefined,
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: undefined,
          token: storedTokens.token,
          refreshToken: storedTokens.refreshToken,
          idToken: storedTokens.idToken,
        };
        return originalInit(modifiedOptions);
      } catch (err) {
        testLog('Failed to restore tokens from storage:', err);
      }
    }

    // No stored tokens available, this will trigger login flow
    // After successful login, this browser becomes the primary browser
    return originalInit(initOptions);
  };

  // Keep server cache updated when keycloak refreshes tokens
  keycloakInstanceObj.onAuthRefreshSuccess = (): void => {
    testLog('Token refresh success, updating server cache');
    saveTokensToStorage({
      token: keycloakInstanceObj.token,
      refreshToken: keycloakInstanceObj.refreshToken,
      idToken: keycloakInstanceObj.idToken,
      tokenParsed: keycloakInstanceObj.tokenParsed,
      idTokenParsed: keycloakInstanceObj.idTokenParsed,
      refreshTokenParsed: keycloakInstanceObj.refreshTokenParsed,
      timeSkew: keycloakInstanceObj.timeSkew,
    }).catch((e) => {
      testLog('Failed to update tokens to server after refresh:', e);
    });
  };
}

export const keycloakInstance = keycloakInstanceObj;

// ts-unused-exports:disable-next-line
export const getKeycloakUserId = (): string | undefined => {
  const kcToken: KeycloakTokenExt = keycloakInstance?.tokenParsed || {};
  return kcToken.keycloak_user_id ?? kcToken.sub;
};

const getConnectCarrierKeycloakRolesFromCookie = (): string[] | undefined => {
  const match = document.cookie.match(
    new RegExp('(^| )' + 'kc_roles' + '=([^;]+)'),
  );
  if (match) {
    try {
      const roles = jsonParse(decodeURIComponent(match[2] ?? ''));
      if (Array.isArray(roles)) {
        return roles;
      }
    } catch {
      // noop
    }
  }
  return undefined;
};

/** WARNING This is for connect-carrier only. Roles are not accessible here for MasterMind. Use Permissions setup, see Mercator. */
// ts-unused-exports:disable-next-line
export const getConnectCarrierKeyloakRoles = (): string[] | undefined => {
  if (IS_CONNECT_CARRIER_LOCAL_OR_CI_CYPRESS) {
    return getConnectCarrierKeycloakRolesFromCookie();
  }
  const kcToken: KeycloakTokenExt = keycloakInstance?.tokenParsed || {};
  return kcToken?.realm_access?.roles;
};

/** WARNING This is for connect-customer only. Roles are not accessible here for MasterMind. Use Permissions setup, see Mercator. */
// ts-unused-exports:disable-next-line
export const getConnectCustomerKeyloakRoles = (): string[] | undefined => {
  if (IS_CONNECT_CUSTOMER_LOCAL_OR_CI_CYPRESS) {
    return getConnectCarrierKeycloakRolesFromCookie(); // TODO: Update for customer-connect later
  }
  const kcToken: KeycloakTokenExt = keycloakInstance?.tokenParsed || {};
  return kcToken?.realm_access?.roles;
};

// ts-unused-exports:disable-next-line
export const useKeycloakJWTInfo = (): KeycloakTokenExt => {
  return keycloakInstance?.tokenParsed || {};
};

// monkeypatching the init method, it allows a developer to SKIP_AUTH with Keycloak.

if (SKIP_AUTH) {
  keycloakInstance.init = function (
    initOptions: KeycloakInitOptions,
  ): Promise<boolean> {
    noop(initOptions);
    this.onAuthSuccess && this.onAuthSuccess();
    return new Promise<boolean>(noop);
  };
}

const keycloakProviderInitConfig: KeycloakInitOptions = {
  onLoad: 'check-sso',
  enableLogging: IS_NOT_PROD_ENV ? false : true,
  silentCheckSsoRedirectUri: win.location.origin + '/silent-check-sso.html',
};

const clearLocalStorage = (): void => {
  const swrWhoAmIStorageKeys = Object.keys(win.localStorage).filter(
    (key) =>
      key.startsWith('swr-query whoAmI') ||
      key.startsWith('swr-err@query whoAmI'),
  );
  swrWhoAmIStorageKeys.forEach(removeLocalStorage);
  removeLocalStorage(LOCAL_STORAGE_LD_USER_IDENTIFIERS);
  // Fire off token clearing (async but don't wait)
  clearStoredTokens().catch(() => {
    // Ignore errors during cleanup
  });
};

export const MasteryKeycloakProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const enableDelayLoading = useFlagMe418020EnableUserProfileGateBeforeRender();
  // would be better to use useReducer instead of fragmenting state across different hooks but im lazy
  const [user, setUser] = useState<MMKeycloakProfile | null>(
    defaultContext.user,
  );
  const [status, setStatus] = useState<AuthStatus>(defaultContext.status);
  const [hasLdIdentified, setHasLdIdentified] = useState<boolean>(
    defaultContext.ldIdentified,
  );
  const ldClient = useLDClient();
  const [refreshTokenInterval, setRefreshTokenInterval] = useState<
    number | undefined
  >();

  const minValiditySeconds =
    getGlobalVariable<number>(APP_KEYCLOAK_MIN_VALIDITY_SECONDS_SYMBOL) ||
    flagDefaultsForLocalDev['app-keycloak-min-validity-seconds'] ||
    30000;
  const triggerRefreshSeconds =
    getGlobalVariable<number>(APP_KEYCLOAK_TRIGGER_REFRESH_SECONDS_SYMBOL) ||
    flagDefaultsForLocalDev['app-keycloak-trigger-refresh-seconds'] ||
    240;

  const clearTokenRefresh = useCallback((): void => {
    if (refreshTokenInterval || keycloakInstance.idToken) {
      testLog('Clear token refresh interval, tokens, and WhoAmI localStorage.');
      clearInterval(refreshTokenInterval);
      setRefreshTokenInterval(undefined);
      clearLocalStorage();
    }
  }, [refreshTokenInterval]);

  const loadData = useCallback((): void => {
    setStatus(AuthStatus.authenticated);
    const userKey = getKeycloakUserId() || '';
    if (keycloakInstance.authenticated && keycloakInstance.token) {
      keycloakInstance
        .loadUserProfile()
        .then(async (result) => {
          setUser(result);
          if (!hasLdIdentified) {
            try {
              await setUserData({
                ldClient,
                data: {
                  email: result.email,
                  key: userKey,
                  hostname: win.location.hostname,
                  // TODO: this is not good, we should use a real .name value if we can get one
                  name: `${result.firstName} ${result.lastName}`,
                },
              });
              setHasLdIdentified(true);
            } catch (err: anyOk) {
              reportCustomError(err);
            }
          }
        })
        .catch(() => {
          testLog(
            `Loading the user's profile from Keycloak failed.  Session may have ended.`,
          );
          keycloakInstance.logout();
        });
    } else {
      testLog(
        `Not loading user data: authenticated ${
          keycloakInstance.authenticated
        }, token ${!!keycloakInstance.token}`,
      );
    }
  }, [ldClient, hasLdIdentified]);

  const updateToken = useCallback((): void => {
    try {
      if (keycloakInstance.authenticated && keycloakInstance.token) {
        const tokenExpired =
          keycloakInstance.isTokenExpired(minValiditySeconds);
        keycloakInstance
          .updateToken(minValiditySeconds)
          .then(() => {
            testLog('UpdateToken successful. Call loadData next;');
            loadData();
          })
          .catch(() => {
            if (!tokenExpired) {
              reportCustomError(
                'Failed to refresh token. keycloakInstance.updateToken on non-expired token FAILED.',
              );
              testLog(
                'Failed to refresh token. Error reported.  keycloakInstance.updateToken on non-expired token FAILED.',
                new Date().toISOString(),
              );
            } else {
              testLog('Failed to refresh token.', new Date().toISOString());
            }
          });
      }
    } catch {
      clearTokenRefresh();
      testLog(
        'An error ocured while updating the token and loading user data from Keycloak.',
      );
    }
  }, [loadData, clearTokenRefresh, minValiditySeconds]);

  const onKeycloakEvent = useCallback(
    (event: AuthClientEvent, error: AuthClientError | undefined): void => {
      testLog('onKeycloakEvent:', event, new Date().toISOString());
      if (error) {
        testLog('Reporting error:', error);
        reportCustomError(jsonStringify(error));
        keycloakInstance.logout();
        return;
      }

      if (SKIP_AUTH) {
        setStatus(AuthStatus.authenticated);
        const user: MMKeycloakProfile = {
          email: 'test@mastery.net',
          firstName: 'Test',
          lastName: 'User',
          username: '1234',
          emailVerified: true,
          attributes: {
            Orgs: [jsonStringify({ id: '1', hierarchyCode: '1234' })],
          },
        };
        setUser(user);
        return;
      }

      const eventMap: Record<AuthClientEvent, () => void> = {
        onAuthSuccess: () => {
          const CHECK_TOKEN_INTERVAL_MS = triggerRefreshSeconds * 1000;
          if (HAS_MASTERY_CHROME_EXTENSION) {
            updateToken();
          } else {
            loadData();
          }
          setRefreshTokenInterval(
            win.setInterval(() => {
              try {
                testLog('Update token on refresh.');
                updateToken();
              } catch {
                testLog('Update token failed on refresh.');
              }
            }, CHECK_TOKEN_INTERVAL_MS),
          );
        },
        onAuthLogout: clearTokenRefresh,
        onAuthError: clearTokenRefresh,
        onAuthRefreshError: () => {
          testLog(
            "User's Keycloak session expired but Keycloak tried to refresh the token.  We've patched keycloak-js to prevent error logging in Sentry.",
          );
          clearTokenRefresh();
        },
        onInitError: noop,
        onTokenExpired: noop,
        onAuthRefreshSuccess: noop,
        onReady: noop,
      };

      eventMap[event]();
    },
    [clearTokenRefresh, loadData, triggerRefreshSeconds, updateToken],
  );

  const onTokensHandler = useCallback(
    (tokens: Pick<AuthClient, 'idToken' | 'refreshToken' | 'token'>): void => {
      noop(tokens);
      testLog('Have token.');

      // Save tokens to server for cross-browser reuse in local dev
      if (IS_LOCAL_DEV && !SKIP_AUTH) {
        // Only mark as primary browser if this browser performed the actual login
        // (not if it restored tokens from the server)
        if (!didRestoreTokensFromServer) {
          markAsPrimaryBrowser();
        }

        saveTokensToStorage({
          token: keycloakInstance.token,
          refreshToken: keycloakInstance.refreshToken,
          idToken: keycloakInstance.idToken,
          tokenParsed: keycloakInstance.tokenParsed,
          idTokenParsed: keycloakInstance.idTokenParsed,
          refreshTokenParsed: keycloakInstance.refreshTokenParsed,
          timeSkew: keycloakInstance.timeSkew,
        }).catch(() => {
          // Ignore errors during token save
        });
      }
    },
    [],
  );

  const isLoadingProfile = keycloakInstance.authenticated && !user;

  const authContextValue = useMemo((): AuthState => {
    let orgs = [];
    try {
      orgs = user?.attributes?.Orgs?.map(jsonParse);
    } catch {
      // noop
    }
    return { status, user: { ...user, orgs }, ldIdentified: hasLdIdentified };
  }, [status, user, hasLdIdentified]);

  return (
    <ReactKeycloakProvider
      authClient={keycloakInstance}
      initOptions={keycloakProviderInitConfig} //object to be passed to keycloak.init()
      onTokens={onTokensHandler} //handler function receives keycloak tokens as an object every time they change.
      onEvent={onKeycloakEvent}
    >
      <AuthContext.Provider value={authContextValue}>
        {enableDelayLoading && isLoadingProfile ? null : children}
      </AuthContext.Provider>
    </ReactKeycloakProvider>
  );
};
